import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getConsent, CONSENT_EVENT } from '../lib/cookieConsent';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const SCRIPT_ID = 'esganga-meta-pixel';
const FB_ROOT_ID = 'fb-root';

function isMarketingAllowed() {
    return Boolean(getConsent()?.marketing);
}

function loadPixel() {
    if (!PIXEL_ID || document.getElementById(SCRIPT_ID)) {
        return;
    }

    let fbRoot = document.getElementById(FB_ROOT_ID);
    if (!fbRoot) {
        fbRoot = document.createElement('div');
        fbRoot.id = FB_ROOT_ID;
        document.body.appendChild(fbRoot);
    }

    if (window.fbq) {
        return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';

    window.fbq = function fbq(...args) {
        if (window.fbq.callMethod) {
            window.fbq.callMethod(...args);
        } else {
            window.fbq.queue.push(args);
        }
    };
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    window.fbq.queue = [];

    script.onload = () => {
        window.fbq('init', PIXEL_ID);
        window.fbq('track', 'PageView');
    };

    document.head.appendChild(script);
}

function unloadPixel() {
    const script = document.getElementById(SCRIPT_ID);
    if (script) {
        script.remove();
    }
    window.fbq = undefined;
}

export default function MetaPixel() {
    const location = useLocation();
    const lastPathRef = useRef(location.pathname + location.search);

    useEffect(() => {
        if (!PIXEL_ID) return undefined;

        if (isMarketingAllowed()) {
            loadPixel();
        }

        const handleConsentChange = (event) => {
            if (event.detail?.marketing) {
                loadPixel();
            } else {
                unloadPixel();
            }
        };

        window.addEventListener(CONSENT_EVENT, handleConsentChange);
        return () => window.removeEventListener(CONSENT_EVENT, handleConsentChange);
    }, []);

    useEffect(() => {
        if (!PIXEL_ID || !window.fbq || !isMarketingAllowed()) {
            return;
        }
        const nextPath = location.pathname + location.search;
        if (lastPathRef.current === nextPath) {
            return;
        }
        lastPathRef.current = nextPath;
        window.fbq('track', 'PageView');
    }, [location.pathname, location.search]);

    return null;
}

export function trackMetaEvent(eventName, params = {}) {
    if (!PIXEL_ID || !window.fbq || !isMarketingAllowed()) {
        return;
    }
    window.fbq('track', eventName, params);
}

export function trackMetaCustomEvent(eventName, params = {}) {
    if (!PIXEL_ID || !window.fbq || !isMarketingAllowed()) {
        return;
    }
    window.fbq('trackCustom', eventName, params);
}
